import {
	bimester,
	currentPeriodType,
	day,
	halfYear,
	month,
	quarter,
	week,
	year,
	yearToDate,
} from "../../../models/period/PeriodType.js";
import { select } from "../../../core/elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";

const subPeriodTypes = [day, week, month];

export const defaultPeriodTypes = [
	currentPeriodType,
	month,
	quarter,
	halfYear,
	year,
];

/**
 * Widget allowing the user to pick a type of period.
 *
 * @param {PeriodType[]} periodTypes - List of period types to render, sorted from smaller to bigger
 * @param {string} [selectedPeriodTypeId] - Id of the initially selected item
 * @param {PeriodType} [minimumPeriodType] - Smallest period type to include in the list
 * @param {function} [onInitialize] - Callback triggered when initializing the dropdown. Invoked with the initially selected item
 * @param {function} [onChange] - Callback triggered when the selection changes. Invoked with the freshly selected item
 */
export default class PeriodTypePicker extends Widget {
	constructor({
		periodTypes,
		selectedPeriodTypeId = null,
		minimumPeriodType = undefined,
		onInitialize = () => {},
		onChange = () => {},
	} = {}) {
		super(...arguments);

		this._basePeriodTypes = periodTypes;
		this._minimumPeriodType = minimumPeriodType;

		const periods = this._getSelectItems({ selectedPeriodTypeId });
		const selectedPeriodType = periods.find((item) => item.selected);

		this._selectedPeriod = selectedPeriodType?.value;

		this._periods = periods;
		this._onChange = onChange;

		if (this._selectedPeriod) {
			onInitialize(this._selectedPeriod);
		}
	}

	setMinimumPeriodType(periodType) {
		this._onChange(this.setMinimumPeriodTypeSilently(periodType));
	}

	setMinimumPeriodTypeSilently(periodType) {
		this._minimumPeriodType = periodType;
		this._periods = this._getSelectItems();
		this._selectedPeriod = this._periods[0].value;

		this.update();
		return this._selectedPeriod;
	}

	renderContentOn(html) {
		html.render(
			select({
				ref: (ref) => ref.setAttribute("name", "period-type-picker"),
				items: this._periods,
				selected: this._selectedPeriod,
				onChange: this._onChange,
			}),
		);
	}

	_getSelectItems({ selectedPeriodTypeId } = {}) {
		const periodTypes = this._getFilteredElements();
		return periodTypes.map((periodType) => ({
			label: periodType.getName(),
			value: periodType,
			selected: periodType.getId() === selectedPeriodTypeId,
		}));
	}

	_getFilteredElements() {
		if (!this._minimumPeriodType) {
			return this._basePeriodTypes;
		}

		if (this._minimumPeriodType === yearToDate) {
			return [month, year];
		}

		const slicedIndex = this._basePeriodTypes.findIndex(
			(each) => each.getId() === this._minimumPeriodType.getId(),
		);
		return this._basePeriodTypes
			.slice(slicedIndex)
			.filter((each) => each !== yearToDate);
	}

	static forCommonTypes({
		isBimesterEnabled,
		isYearToDateEnabled,
		spec = {},
	} = {}) {
		const periodTypes = [...defaultPeriodTypes];
		if (isBimesterEnabled) {
			periodTypes.splice(periodTypes.indexOf(month), 0, bimester);
		}
		if (isYearToDateEnabled) {
			periodTypes.push(yearToDate);
		}
		return new PeriodTypePicker({ ...spec, periodTypes });
	}

	static forSubTypes(spec) {
		return new PeriodTypePicker({
			...spec,
			periodTypes: subPeriodTypes,
		});
	}
}
